import { useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, GraduationCap, Building2, Pencil, Trash2, RotateCcw } from "lucide-react";

const FONTE = { vendita: "Vendita", ordine: "Ordine", iscrizione: "Iscrizione" };

export default function Allievi() {
  const { user } = useAuth();
  const isOwner = user.role !== "partner";
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [query, setQuery] = useState("");
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/allievi").then((r) => setItems(r.data)).catch(() => {});
  const loadHidden = () => api.get("/allievi/nascosti").then((r) => setHidden(r.data)).catch(() => {});
  useEffect(() => { load(); loadHidden(); }, []);

  const openEdit = (a) => setEdit({
    fonte: a.fonte, fonte_id: a.fonte_id, ri: a.ri ?? -1, ci: a.ci ?? 0,
    nome: a.nome || "", cognome: a.cognome || "", codice_fiscale: a.codice_fiscale || "",
    email: a.email || "", telefono: a.telefono || a.cellulare || "",
    data_nascita: a.data_nascita || "", luogo_nascita: a.luogo_nascita || "",
    provincia_nascita: a.provincia_nascita || "",
  });
  const setF = (k, v) => setEdit((e) => ({ ...e, [k]: v }));
  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put("/allievi", edit);
      toast.success("Anagrafica aggiornata");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) =>
      [a.nome, a.cognome, a.codice_fiscale, a.azienda_nome, a.corso, a.partner_nome]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [items, query]);

  const scarica = async (a) => {
    if (!window.confirm(`Rimuovere ${(a.cognome + " " + a.nome).trim() || "questo allievo"} dall'anagrafica? I dati restano negli ordini e nei documenti collegati.`)) return;
    try {
      await api.post("/allievi/nascondi", { codice_fiscale: a.codice_fiscale || "", nome: a.nome || "", cognome: a.cognome || "" });
      setItems((list) => list.filter((x) => x !== a));
      toast.success("Allievo rimosso dall'anagrafica");
      loadHidden();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ripristina = async (h) => {
    try {
      await api.post("/allievi/mostra", { codice_fiscale: h.codice_fiscale || "", nome: h.nome || "", cognome: h.cognome || "" });
      setHidden((list) => list.filter((x) => x !== h));
      toast.success("Allievo ripristinato in anagrafica");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="allievi-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{isOwner ? "Allievi / Clienti" : "I Miei Allievi"}</h1>
          <p className="text-sm text-slate-500 mt-1">Anagrafica dei corsisti da vendite, ordini e iscrizioni. Ogni allievo compare una sola volta ed è modificabile.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="allievi-search"
            placeholder="Cerca per nome, CF, azienda o corso…" className="pl-9 bg-white" />
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left border-b border-slate-200">
                <th className="py-3 px-4 font-medium">Corsista</th>
                <th className="py-3 px-4 font-medium">Codice Fiscale</th>
                <th className="py-3 px-4 font-medium">Azienda</th>
                <th className="py-3 px-4 font-medium">Corso</th>
                {isOwner && <th className="py-3 px-4 font-medium">Partner</th>}
                <th className="py-3 px-4 font-medium">Fonte</th>
                <th className="py-3 px-4 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`allievo-row-${i}`}>
                  <td className="py-2.5 px-4">
                    <div className="font-medium text-slate-800 flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-dusty-500" />{(a.cognome + " " + a.nome).trim() || "—"}</div>
                    {(a.telefono || a.email) && <div className="text-xs text-slate-400">{[a.telefono, a.email].filter(Boolean).join(" · ")}</div>}
                  </td>
                  <td className="py-2.5 px-4 text-slate-600">{a.codice_fiscale || "—"}</td>
                  <td className="py-2.5 px-4 text-slate-600"><span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3 text-slate-400" />{a.azienda_nome || "—"}</span></td>
                  <td className="py-2.5 px-4 text-slate-600">{a.corso || "—"}</td>
                  {isOwner && <td className="py-2.5 px-4 text-slate-600">{a.partner_nome || "—"}</td>}
                  <td className="py-2.5 px-4"><span className="text-xs px-2 py-0.5 rounded-full border bg-dusty-50 text-dusty-700 border-dusty-200">{FONTE[a.fonte] || a.fonte}</span></td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <button onClick={() => openEdit(a)}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold"
                        data-testid={`allievo-edit-${i}`}>
                        <Pencil className="h-3.5 w-3.5" /> Modifica
                      </button>
                      <button onClick={() => scarica(a)}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 font-semibold"
                        data-testid={`allievo-delete-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isOwner ? 7 : 6} className="py-14 text-center text-slate-400" data-testid="allievi-empty">
                  {items.length === 0 ? "Nessun allievo registrato. I corsisti compaiono qui dopo vendite, ordini o iscrizioni." : `Nessun allievo corrisponde a «${query}».`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {hidden.length > 0 && (
        <div data-testid="allievi-hidden-section">
          <button onClick={() => setShowHidden((v) => !v)} data-testid="allievi-hidden-toggle"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
            <RotateCcw className="h-4 w-4" /> Allievi rimossi ({hidden.length}) {showHidden ? "▲" : "▼"}
          </button>
          {showHidden && (
            <Card className="border-slate-200 shadow-sm overflow-hidden mt-3">
              <div className="divide-y divide-slate-100">
                {hidden.map((h, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`allievo-hidden-row-${i}`}>
                    <div className="text-sm text-slate-700">
                      <span className="font-medium">{(h.cognome + " " + h.nome).trim() || "—"}</span>
                      {h.codice_fiscale && <span className="text-slate-400 ml-2 text-xs">{h.codice_fiscale}</span>}
                    </div>
                    <button onClick={() => ripristina(h)} data-testid={`allievo-restore-${i}`}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-dusty-300 text-dusty-800 hover:bg-dusty-50 font-semibold">
                      <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="allievo-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Modifica anagrafica allievo</DialogTitle>
            <DialogDescription>Le modifiche vengono salvate sulla fonte originale ({edit && (FONTE[edit.fonte] || edit.fonte)}).</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
              <div><Label>Nome</Label><Input value={edit.nome} onChange={(e) => setF("nome", e.target.value)} className="mt-1.5" data-testid="allievo-edit-nome" /></div>
              <div><Label>Cognome</Label><Input value={edit.cognome} onChange={(e) => setF("cognome", e.target.value)} className="mt-1.5" data-testid="allievo-edit-cognome" /></div>
              <div className="sm:col-span-2"><Label>Codice Fiscale</Label><Input value={edit.codice_fiscale} onChange={(e) => setF("codice_fiscale", e.target.value.toUpperCase())} className="mt-1.5" data-testid="allievo-edit-cf" /></div>
              <div><Label>Email</Label><Input value={edit.email} onChange={(e) => setF("email", e.target.value)} className="mt-1.5" data-testid="allievo-edit-email" /></div>
              <div><Label>Telefono</Label><Input value={edit.telefono} onChange={(e) => setF("telefono", e.target.value)} className="mt-1.5" data-testid="allievo-edit-telefono" /></div>
              <div><Label>Data di nascita</Label><Input type="date" value={edit.data_nascita} onChange={(e) => setF("data_nascita", e.target.value)} className="mt-1.5" data-testid="allievo-edit-nascita" /></div>
              <div><Label>Luogo di nascita</Label><Input value={edit.luogo_nascita} onChange={(e) => setF("luogo_nascita", e.target.value)} className="mt-1.5" data-testid="allievo-edit-luogo" /></div>
              <div><Label>Provincia di nascita</Label><Input value={edit.provincia_nascita} onChange={(e) => setF("provincia_nascita", e.target.value.toUpperCase())} maxLength={2} className="mt-1.5" data-testid="allievo-edit-prov" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="allievo-edit-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">{saving ? "Salvo…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
